import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiMessageSquare, FiStar } from "react-icons/fi";

/* ===== Components ===== */
import PageHeader from "../../components/common/PageHeader";
import ExperienceInfo from "../../components/UserComponents/ExperienceInfo";
import VendorInfo from "../../components/UserComponents/VendorInfo";
import CustomButton from "../../components/common/CustomButton";
import BookingStatusTimeline from "../../components/common/BookingStatusTimeline";
import SuggestAlternateTimeModal from "../../components/vendors/SuggestAlternateTime";

/* ===== Hooks / Queries ===== */
import {
  useUserAcceptAlternateTime,
  useUserDenyAlternateTime,
  useUserBookingById,
  useUserBookingComplete,
  useSubmitBookingReview,
  useBookingCalendar,
  useBookingCancel,
} from "../../custom-hooks/queries";

/* ===== Utils ===== */
import {
  downloadICSFile,
  formatDate,
  getCleanedUrl,
} from "../../utils/HelperMethod";
import LeaveReviewModal from "../../components/UserComponents/ReviewModal";
import StatusBadge from "../../components/vendors/StatusBadge";
import { SHOW_BUTTON_STATUSES } from "../../utils/constants";

/* ================= SKELETON ================= */
const BookingDetailSkeleton = () => (
  <div className="w-full max-w-[1380px] mx-auto px-6 py-10 animate-pulse">
    <div className="h-8 w-64 bg-gray-200 rounded mb-6"></div>
    <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-10">
      <div className="h-[320px] bg-gray-200 rounded-xl"></div>
      <div className="h-[400px] bg-gray-200 rounded-xl"></div>
    </div>
  </div>
);

/* ================= PAGE ================= */
export default function UserBookingDetailPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const {
    mutate: calendarData,
    isPending: isCalendarLoading,
    isError: isCalendarError,
  } = useBookingCalendar(bookingId);

  const [showSuggestedTime, setShowSuggestedTime] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  /* ===== Mutations ===== */
  const { mutate: acceptBooking, isPending: isAccepting } =
    useUserAcceptAlternateTime();

  const { mutate: denyBooking, isPending: isDenying } =
    useUserDenyAlternateTime();

  const { mutate: completeBooking, isPending: isCompleting } =
    useUserBookingComplete();

  const { mutate: submitReview, isPending: isSubmittingReview } =
    useSubmitBookingReview();

  const { mutate: cancelBooking, isPending: isCancelling } = useBookingCancel();

  /* ===== Booking Detail ===== */
  const { data, isLoading, isError, refetch } = useUserBookingById(bookingId);

  const booking = data?.data || data || {};
  const experience = booking?.experience || {};
  const vendor = booking?.vendor || {};
  const currentStatus = booking?.currentStatus;
  const canCancel = booking?.canCancel;
  const price = experience?.price;

  const vendorLogo = getCleanedUrl(vendor?.logo) || "";

  const normalizedStatus = booking?.currentStatus?.toLowerCase()?.trim();

  if (isLoading) return <BookingDetailSkeleton />;

  if (isError || !booking?.bookingId) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl font-semibold">Booking not found</h2>
        <button
          onClick={() => navigate("/user/my-bookings")}
          className="mt-6 px-6 py-2 bg-[#00C3CC] text-white rounded-lg"
        >
          Go to My Bookings
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1380px] mx-auto px-6 py-10">
      {/* ================= HEADER ================= */}
      <div className="flex justify-between items-center mb-6">
        <PageHeader title={experience.title} />

        <div className="flex items-center justify-end gap-3">
          {canCancel && (
            <CustomButton
              variant="secondary"
              loading={isCancelling}
              onClick={() =>
                cancelBooking(
                  { bookingId },
                  {
                    onSuccess: () => {
                      toast.success("Canceled booking successfully");
                      refetch();
                    },
                    onError: (error) => {
                      toast.error(
                        error?.response?.data?.error_message ||
                          "Failed to cancel booking",
                      );
                    },
                  },
                )
              }
            >
              Cancel Booking
            </CustomButton>
          )}

          {currentStatus === "Vendor Proposed Alternate Time" && (
            <div className="flex gap-2">
              <CustomButton
                variant="secondary"
                onClick={() => setShowSuggestedTime(true)}
              >
                View Suggested Time
              </CustomButton>

              <CustomButton
                variant="primary"
                loading={isAccepting}
                onClick={() =>
                  acceptBooking(
                    { bookingId },
                    {
                      onSuccess: () => {
                        toast.success("Suggested time accepted");
                        refetch();
                      },
                      onError: () =>
                        toast.error("Failed to accept suggested time"),
                    },
                  )
                }
              >
                Accept
              </CustomButton>

              <CustomButton
                variant="secondary"
                loading={isDenying}
                onClick={() =>
                  denyBooking(
                    { bookingId },
                    {
                      onSuccess: () => {
                        toast.success("Suggested time rejected");
                        refetch();
                      },
                      onError: () =>
                        toast.error("Failed to reject suggested time"),
                    },
                  )
                }
              >
                Reject
              </CustomButton>
            </div>
          )}

          {currentStatus === "Awaiting User Confirmation" && (
            <CustomButton
              variant="primary"
              loading={isCompleting}
              onClick={() =>
                completeBooking(
                  { bookingId },
                  {
                    onSuccess: () => {
                      toast.success("Booking completed");
                      refetch();
                    },
                    onError: () => toast.error("Failed to complete booking"),
                  },
                )
              }
            >
              Complete
            </CustomButton>
          )}

          {SHOW_BUTTON_STATUSES.includes(normalizedStatus) && (
            <CustomButton
              onClick={() =>
                calendarData(bookingId, {
                  onSuccess: (icsData) => {
                    downloadICSFile(icsData, `booking-${bookingId}.ics`);
                    toast.success("Calendar file downloaded");
                  },
                  onError: (error) => {
                    toast.error(
                      error?.response?.data?.message ||
                        "Failed to complete the action.",
                    );
                  },
                })
              }
              variant="primary"
              loading={isCalendarLoading}
            >
              Add to Calendar
            </CustomButton>
          )}
        </div>
      </div>

      {/* ================= CONTENT ================= */}
      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-10">
        {/* LEFT */}
        <div>
          {experience.images?.[0] && (
            <img
              src={experience.images[0]}
              alt={experience.title}
              className="w-full h-[320px] rounded-xl object-stretch object-center"
            />
          )}

          <div className="mt-10">
            <ExperienceInfo
              title={experience.title}
              rating={experience.rating}
              reviewsCount={experience.totalReviews}
              location={experience.location}
              price={price}
              duration={experience.rating}
              category={experience.category}
              description={experience.description}
              priceType={experience.priceType}
            />
          </div>

          {vendor.businessName && (
            <div className="mt-10">
              <VendorInfo
                name={vendor.businessName}
                description={vendor.description}
                rating={vendor.rating}
                logoUrl={vendorLogo}
                totalReviews={vendor.totalReviews}
              />
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div className="border rounded-2xl p-6 h-fit">
          <h2 className="text-lg font-semibold mb-5">Booking Summary</h2>

          <SummaryRow label="Booking ID" value={booking.bookingCode} />
          <SummaryRow
            label="Booking Date"
            value={formatDate(booking.bookingDate)}
          />
          <SummaryRow label="Time Slot" value={booking.timeSlot} />
          <SummaryRow
            label="Number of People"
            value={`${booking?.numberOfPeople || 0}`}
          />
          <SummaryRow label="Payment Method" value={booking.paymentMethod} />

          <div className="flex justify-between mt-4">
            <span className="font-semibold text-md">Payment Status</span>
            <StatusBadge status={booking.paymentStatus} />
          </div>

          <hr className="my-6" />

          <BookingStatusTimeline
            title="Booking Status"
            timeline={booking.userBookingstatusTimeline || []}
          />

          {booking?.experience?.importantNotes && (
            <>
              <hr className="my-6" />
              <h2 className="text-lg font-semibold mb-4">Notes</h2>
              <p className="text-sm text-gray-700">
                • {booking.experience.importantNotes}
              </p>
            </>
          )}

          <hr className="my-6" />

          {/* CONTACT SUPPORT */}
          <button
            className="w-full border rounded-xl py-3 flex items-center gap-3 px-4 hover:bg-gray-100"
            onClick={() => navigate("/user/support")}
          >
            <FiMessageSquare size={18} />
            Contact Support
          </button>

          {/* LEAVE REVIEW */}
          {currentStatus === "Completed" && (
            <button
              className="w-full border rounded-xl py-3 flex items-center gap-3 px-4 hover:bg-gray-100 mt-3"
              onClick={() => setShowReviewModal(true)}
            >
              <FiStar size={18} />
              Leave Review
            </button>
          )}
        </div>
      </div>

      {/* ================= MODALS ================= */}

      {/* VIEW SUGGESTED TIME */}
      <SuggestAlternateTimeModal
        isOpen={showSuggestedTime}
        onClose={() => setShowSuggestedTime(false)}
        mode="view"
        initialSuggestions={booking?.alternateTiming || []}
      />

      {/* LEAVE REVIEW */}
      <LeaveReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        isSubmitting={isSubmittingReview}
        onSubmit={({ rating, review }) =>
          submitReview(
            { bookingId, rating, review },
            {
              onSuccess: () => {
                toast.success("Review submitted successfully");
                setShowReviewModal(false);
              },
              onError: () => {
                toast.error("Failed to submit review");
              },
            },
          )
        }
      />
    </div>
  );
}

/* ================= SMALL HELPER ================= */

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1 mt-3">
      <span className="text-black font-semibold text-[16px]">{label}</span>
      <span className="">{value}</span>
    </div>
  );
}
